import { UserDjSoftwareEnum } from '@cratehackers/api-client';
import { IonButton, IonContent, IonModal, IonText } from '@ionic/react';
import { useState } from 'react';
import { FolderManager } from '../common/FolderManager';
import './Wizard.scss';
import SelectDJSoftwareStep from './steps/SelectDJSoftwareStep';
import SelectPoolStep from './steps/SelectPoolStep';

interface Props {
  onComplete: (route: string) => void;
}

export default function SetupWizard({ onComplete }: Props) {
  const finish = (route: string) => {
    try {
      const refreshEvent = new CustomEvent('library-refresh', {
        detail: { reason: 'wizard-completed' },
      });
      window.dispatchEvent(refreshEvent);
    } finally {
      onComplete(route);
    }
  };

  const [step, setStep] = useState(1);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [currentSkipRoute, setCurrentSkipRoute] = useState('/');

  const openSkipModal = () => setShowSkipModal(true);
  const closeSkipModal = () => setShowSkipModal(false);
  const confirmAndSkip = () => {
    closeSkipModal();
    finish(currentSkipRoute);
  };

  const handleSoftwareSelectionComplete = (software: UserDjSoftwareEnum[]) => {
    setStep(2);
  };

  const handlePoolSelectionComplete = () => {
    setStep(3);
  };

  const handleSkipSetup = () => {
    setCurrentSkipRoute('/my-crates/spotify');
    openSkipModal();
  };

  return (
    <IonContent fullscreen className="wizard-container">
      {/* Step 1: Let users pick the DJ software they care about */}
      {step === 1 && (
        <SelectDJSoftwareStep
          onNext={handleSoftwareSelectionComplete}
          onSkip={handleSkipSetup}
        />
      )}
      {/* Step 2: Let users choose which music pools to include */}
      {step === 2 && (
        <SelectPoolStep
          onNext={handlePoolSelectionComplete}
          onSkip={handleSkipSetup}
        />
      )}

      {/* Step 3: Always add folders inside the wizard */}
      {step === 3 && (
        <div className="wizard-step">
          <FolderManager
            mode="wizard"
            description="Where do you keep your music?"
            onComplete={() => finish('/')}
            onSkip={() => finish('/')}
            showSkipOption={true}
            showAdvancedOptions={true}
            allowBulkOperations={true}
            autoRefreshLibrary={true}
            className="manual-setup-folder-manager"
          />
        </div>
      )}

      {/* Skip Setup Modal */}
      <IonModal
        isOpen={showSkipModal}
        onDidDismiss={closeSkipModal}
        className="skip-modal"
      >
        <div className="modal-body ion-padding">
          <IonText color="warning" className="warning-text">
            NOTE: You will not get the full&nbsp;Crate&nbsp;Hackers experience
            with track-matching abilities until you scan your library and set
            your software.
          </IonText>

          <div className="button-row">
            <IonButton fill="clear" expand="block" onClick={closeSkipModal}>
              Continue the Setup Process
            </IonButton>

            <IonButton
              expand="block"
              fill="clear"
              color="medium"
              onClick={confirmAndSkip}
            >
              I Understand. Skip the Setup.
            </IonButton>
          </div>
        </div>
      </IonModal>
    </IonContent>
  );
}
